import type { fabric } from 'fabric';
import {
  buildLinearGradient,
  firstStopColor,
  isPixelGradient,
  readLinearGradient,
  resizeLinearGradient,
  splitStopColor,
  type LinearGradientModel,
} from 'shared/fabric';
import type { FabricEditor } from '../core/fabric-editor';
import type { EditorPlugin } from '../interfaces/editor-plugin';
import { FabricPluginName } from './plugin-name';

export interface FillGradientFacade {
  /**
   * The object's linear gradient as angle + stops, read from `fill` itself (the
   * source of truth). Reads any saved template — the editor always wrote the
   * visible gradient into `fill`, so legacy `data.fillGradient` / `data.vignette`
   * are not consulted. `undefined` for a solid fill or an unreadable gradient
   * (radial, `gradientTransform`, offset).
   */
  get(target: fabric.Object): LinearGradientModel | undefined;
  /**
   * Writes the model as a pixel gradient sized to the object's own `width` /
   * `height`, or — with `undefined` — a solid fill of the first stop's color.
   * Silently drops legacy `data.fillGradient` / `data.vignette` first, then makes
   * one `setProp('fill')`: exactly one `object:modified`, one undo step.
   */
  set(target: fabric.Object, model: LinearGradientModel | undefined): void;
  /**
   * Writes `width` / `height` and keeps a pixel gradient `fill` covering the new box
   * (`resizeLinearGradient`, the angle and stops stay) — in one `object:modified`,
   * one undo step. Every programmatic size write in the Media Manager goes through
   * here: a pixel gradient is in object coordinates, and the history plugin snapshots
   * before any other `object:modified` listener could rebuild it. A solid or
   * percentage fill is left as is.
   */
  setSize(target: fabric.Object, size: Partial<Size>): void;
}

interface Size {
  width: number;
  height: number;
}

declare module '../core/fabric-editor' {
  interface FabricEditor {
    fillGradient?: FillGradientFacade;
  }
}

const DEFAULT_COLOR = '#cccccc';

/**
 * Keys the Media Manager stored a gradient copy under before IMGM-1622 (`fillGradient`
 * — two stops and a direction; `vignette` — its older fade-out form). Saved templates
 * still carry them; they are never read, only dropped on the object's next write.
 */
const LEGACY_KEYS = ['fillGradient', 'vignette'] as const;

/**
 * Drops the legacy keys by replacing `data` rather than mutating it — history
 * snapshots may hold the same object reference, and this runs with no
 * `object:modified` emit so the caller's lone `setProp` stays a single undo step.
 */
function stripLegacyKeys(target: fabric.Object): void {
  const data = target.data as Record<string, unknown> | undefined;
  if (!data || !LEGACY_KEYS.some((key) => key in data)) return;
  const next = { ...data };
  LEGACY_KEYS.forEach((key) => delete next[key]);
  target.data = next;
}

/**
 * The first stop's color as a solid fill. An opaque one becomes `#rrggbb` — the only
 * form the inspector's fill picker shows (old templates store `rgba(r, g, b, 1)`).
 */
function solidFillOf(target: fabric.Object): string {
  const first = firstStopColor(target.fill);
  if (!first) return typeof target.fill === 'string' ? target.fill : DEFAULT_COLOR;
  const { hex, alpha } = splitStopColor(first);
  return alpha === 1 ? hex : first;
}

/**
 * Linear gradient fill for fillable shapes (rect, circle, triangle, polygon — any
 * `fabric.Object` whose `fill` is painted), on the model shared with the broadcast
 * board editor (`shared/fabric`, angle as CSS + any number of stops).
 *
 * The plugin patches nothing — a gradient `fill` is native to Fabric and renders and
 * serializes on its own; `fill` is the only source of truth. The plugin is a typed
 * façade (`editor.fillGradient`) so the UI never imports `fabric` values: it reads the
 * model and writes it back as one `setProp('fill')` = one history step.
 */
export function fillGradientPlugin(): EditorPlugin {
  return {
    name: FabricPluginName.FILL_GRADIENT,
    install(editor: FabricEditor) {
      editor.fillGradient = {
        get(target: fabric.Object): LinearGradientModel | undefined {
          return readLinearGradient(target.fill, target.width ?? 0, target.height ?? 0);
        },
        set(target: fabric.Object, model: LinearGradientModel | undefined): void {
          const fill = model ? buildLinearGradient(model, target.width ?? 0, target.height ?? 0) : solidFillOf(target);
          stripLegacyKeys(target);
          target.dirty = true;
          editor.objects.setProp(target, 'fill', fill);
        },
        setSize(target: fabric.Object, size: Partial<Size>): void {
          const from = { width: target.width ?? 0, height: target.height ?? 0 };
          const to = { width: size.width ?? from.width, height: size.height ?? from.height };
          // Set the size silently; the one `setProp` below is the lone emit.
          target.set(to);
          if (isPixelGradient(target.fill)) {
            target.dirty = true;
            editor.objects.setProp(target, 'fill', resizeLinearGradient(target.fill, from, to));
          } else {
            editor.objects.setProp(target, 'width', to.width);
          }
        },
      };
    },
    dispose(editor: FabricEditor) {
      editor.fillGradient = undefined;
    },
  };
}
